<!-- https://www.figma.com/file/JGnLzMLGla9gSsvnW00NUY/Burning-Design-System-%F0%9F%94%A5?node-id=743%3A67432&t=rk5MTxanM2aanWJT-0 -->
<!-- https://www.figma.com/file/hZ14djvbx5qYuIJwSU7moW/%D0%AD%D0%BB%D0%B5%D0%BC%D0%B5%D0%BD%D1%82%D1%8B-%F0%9F%94%A5?type=design&node-id=419-33727&t=LIiiQLKUP8aRVFtN-0 -->
<template>
  <div
    v-if="!isServiceError"
    v-click-outside="handleClickOutside"
    class="reaction-container"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <ReactionButton
      :article-title="articleTitle"
      :hide-count="hideCount"
      :count="allReactionsCount"
      :button-reaction="computedReaction"
      :is-current="true"
      :is-menu-opened="isShowMenu"
      @reaction-button-hold="handleReactionButtonHold"
      @reaction-button-click="handleReactionButtonClick"
    />
    <ReactionMenu
      v-show="isShowMenu && !isSimpleReaction"
      :reactions="reactionFormatted"
      :current-reaction="currentReactionUserState"
      @reaction-click="handleReactionClick"
    />
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import { vOnClickOutside } from '@vueuse/components';
  import ReactionMenu from './ReactionMenu.vue';
  import ReactionButton from './ReactionButton.vue';
  import type {
    AvailableReaction,
    IReactions,
    AnaliticsAction,
  } from '~/types/components/Reactions';
  import type { ReactionResponse } from '~/types/api/reactions';
  import type { TArticle } from '~/types/common';
  import { useServiceErrorsStore } from '~/store/errors';
  import { useReactionsStore } from '~/store/reactions';
  import { useSingleArticleStore } from '~/store/singleArticle';

  const reactionsNameForAnalytics: Record<AvailableReaction, string> = {
    default: '',
    heart: 'сердечко',
    thumbs_up: 'палец вверх',
    on_fire: 'огонь',
    cry: 'плач',
    angry: 'сердитый смайлик',
  };

  export default defineNuxtComponent({
    name: 'TheReaction',

    directives: {
      clickOutside: vOnClickOutside,
    },

    components: {
      ReactionMenu,
      ReactionButton,
    },

    props: {
      hideCount: {
        type: Boolean as PropType<boolean>,
        default: false,
      },

      isSimpleReaction: {
        type: Boolean as PropType<boolean>,
        default: false,
      },

      article: {
        type: Object as PropType<TArticle>,
        default: null,
      },

      analyticsEvent: {
        type: String,
        default: 'Клик_Реакции',
      },
    },

    data: () => ({
      isShowMenu: false,
      isFromMobileMenu: false,
    }),

    computed: {
      reactions() {
        return useReactionsStore().reactions;
      },

      currentArticle() {
        return useSingleArticleStore().article;
      },

      isServiceError() {
        return useServiceErrorsStore().reactionsServiceError;
      },

      allReactionsCount(): number {
        return this.reactionFormatted.reduce(
          (sum: number, { count }: IReactions) => sum + count,
          0,
        );
      },

      articleTitle(): string {
        return this.article?.title || this.currentArticle?.title;
      },

      reactionFormatted(): IReactions[] {
        const reactionTypes: AvailableReaction[] = [
          'heart',
          'thumbs_up',
          'on_fire',
          'cry',
          'angry',
        ];

        return reactionTypes.map((type: AvailableReaction): IReactions => {
          return {
            type,
            count:
              (this.currentReaction?.[`${type}_count` as keyof ReactionResponse] as number) || 0,
          };
        });
      },

      currentReaction(): ReactionResponse {
        // * сначала проверяем есть ли статьи из пропсов, только потом текущую статью по слагу
        const articleId = this.article?.id || this.currentArticle?.id;
        return this.reactions?.[articleId];
      },

      currentReactionUserState(): AvailableReaction | '' {
        return this.currentReaction?.user_state || '';
      },

      computedReaction(): string {
        if (this.isSimpleReaction) {
          return this.currentReactionUserState || 'simple';
        } else {
          return this.currentReactionUserState || 'default';
        }
      },

      slugComputed(): string {
        // * сначала проверяем есть ли статьи из пропсов, только потом текущую статью по слагу
        const slug = this.article?.name || this.currentArticle?.name;
        return slug;
      },
    },

    mounted() {
      useReactionsStore().fetchReactions(this.slugComputed);
    },

    methods: {
      sendAnalyticsEvent(action: AnaliticsAction, type: AvailableReaction): void {
        this.$sendYandexMetrika({
          level1: this.analyticsEvent,
          level5: action,
          level6: reactionsNameForAnalytics[type],
          level8: this.isFromMobileMenu ? 'Реакция из меню на мобилке' : 'Стандартная реакция',
        });
      },

      handleReactionButtonClick(): void {
        this.currentReactionUserState ? this.resetReaction() : this.handleReactionClick('heart');
      },

      handleReactionButtonHold() {
        this.isShowMenu = true;
        this.isFromMobileMenu = true;
      },

      resetReaction(): void {
        if (this.currentReactionUserState) {
          this.sendAnalyticsEvent('Удалили реакцию', this.currentReactionUserState);

          useReactionsStore().updateReaction(this.slugComputed, this.currentReactionUserState);
        }

        this.closePopup();
      },

      handleReactionClick(type: AvailableReaction): void {
        if (type === this.currentReactionUserState) {
          this.resetReaction();
        } else {
          this.sendAnalyticsEvent('Поставили реакцию', type);

          useReactionsStore().updateReaction(this.slugComputed, type);
          this.closePopup();
        }
      },

      closePopup(): void {
        this.isShowMenu = false;
        this.isFromMobileMenu = false;
      },

      handleClickOutside(): void {
        this.closePopup();
      },

      handleMouseEnter(): void {
        this.isShowMenu = true;
      },

      handleMouseLeave(): void {
        this.closePopup();
      },
    },
  });
</script>

<style lang="scss">
  .reaction-container {
    position: relative;
    margin-right: $spacing-2;
  }
</style>
